/**
 * Race Utilities
 *
 * Shared utility functions for race-related calculations.
 * These functions consolidate logic that was previously duplicated across multiple files.
 */

/**
 * Calculate accuracy percentage from correct and incorrect counts.
 * Uses total answers (correct + incorrect) as the denominator.
 *
 * @param correctCount - Number of correct answers
 * @param incorrectCount - Number of incorrect answers
 * @returns Accuracy percentage (0-100)
 */
export function calculateAccuracy(correctCount: number, incorrectCount: number): number {
  const totalAnswers = correctCount + incorrectCount;
  return totalAnswers > 0 ? (correctCount / totalAnswers) * 100 : 0;
}

/**
 * Format race time in milliseconds to a human-readable string.
 *
 * @param ms - Time in milliseconds (can be null/undefined)
 * @returns Formatted string like "1m 30s" or "45s" or "-" if no time
 */
export function formatRaceTime(ms: number | null | undefined): string {
  if (!ms) return '-';
  const seconds = ms / 1000;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

/**
 * Format race time with decimal seconds for more precision.
 *
 * @param ms - Time in milliseconds
 * @returns Formatted string like "1:30.5" or "45.5s"
 */
export function formatRaceTimeWithDecimal(ms: number | null | undefined): string {
  if (!ms) return '-';
  const totalSeconds = ms / 1000;
  const mins = Math.floor(totalSeconds / 60);
  const secs = (totalSeconds % 60).toFixed(1);
  return mins > 0 ? `${mins}:${secs.padStart(4, '0')}` : `${secs}s`;
}

/**
 * Leaderboard entry with required fields for sorting.
 */
export interface LeaderboardSortable {
  correctCount: number;
  totalTime: number | null | undefined;
}

/**
 * Sort leaderboard entries by race ranking criteria:
 * 1. More correct answers = higher rank
 * 2. Among same correct count, faster time wins
 *
 * @param entries - Array of leaderboard entries to sort
 * @returns New sorted array (does not mutate original)
 */
export function sortLeaderboard<T extends LeaderboardSortable>(entries: T[]): T[] {
  return [...entries].sort((a, b) => {
    // More correct answers = higher rank
    if (a.correctCount !== b.correctCount) {
      return b.correctCount - a.correctCount;
    }
    // Among same correct count, faster (lower time) wins
    const aTime = a.totalTime || Infinity;
    const bTime = b.totalTime || Infinity;
    return aTime - bTime;
  });
}

/**
 * Calculate average time per card.
 *
 * @param totalTimeMs - Total elapsed time in milliseconds
 * @param cardsCompleted - Number of cards completed
 * @returns Average time per card in milliseconds, or 0 if no cards
 */
export function calculateAvgTimePerCard(
  totalTimeMs: number | null | undefined,
  cardsCompleted: number
): number {
  if (!totalTimeMs || cardsCompleted === 0) return 0;
  return totalTimeMs / cardsCompleted;
}

/**
 * Assign ranks to sorted leaderboard entries.
 * Entries should already be sorted before calling this function.
 *
 * @param entries - Sorted array of entries
 * @returns Array of entries with rank property added
 */
export function assignRanks<T>(entries: T[]): Array<T & { rank: number }> {
  return entries.map((entry, index) => ({
    ...entry,
    rank: index + 1,
  }));
}
